import { type ReactNode, createContext, useContext } from 'react'

import { useFrameCompletionPercentage } from '@frame-builder/lib/frame'
import {
  type StepPreviewAnimation,
  defaultStepPreviewAnimation,
  useStepPreviewAnimation,
} from '@frame-builder/lib/preview-animation'
import {
  type EnabledFrameBuilderItemMap,
  type FrameBuilderState,
  type ValidationErrorMessages,
} from '@frame-builder/lib/types'
import { useEnabledItemMap } from '@frame-builder/lib/validation/enabled-items'
import { useLastValidFrameBuilderState } from '@frame-builder/lib/validation/last-valid-state'
import { useValidationErrorMessages } from '@frame-builder/lib/validation/error-messages'
import { FrameBuilderSettingsContext } from './settings-context'
import { emptyFrameBuilderState } from './state-context'

interface ValidationContextProps {
  validationErrorMessages: ValidationErrorMessages
  lastValidState: FrameBuilderState
  enabledItemMap: EnabledFrameBuilderItemMap
  frameCompletionPercentage: number
  stepPreviewAnimation: StepPreviewAnimation
}

interface FrameBuilderValidationProviderProps {
  children: ReactNode
}

export const FrameBuilderValidationContext =
  createContext<ValidationContextProps>({
    validationErrorMessages: {},
    lastValidState: emptyFrameBuilderState,
    enabledItemMap: {
      profileIds: [],
      passepartoutColorIds: [],
    },
    frameCompletionPercentage: 0,
    stepPreviewAnimation: defaultStepPreviewAnimation,
  })

export const FrameBuilderValidationProvider = ({
  children,
}: FrameBuilderValidationProviderProps) => {
  const { frameBuilderType } = useContext(FrameBuilderSettingsContext)

  const lastValidState = useLastValidFrameBuilderState()

  const validationErrorMessages = useValidationErrorMessages()

  const stepPreviewAnimation = useStepPreviewAnimation(validationErrorMessages)

  const enabledItemMap = useEnabledItemMap()

  const frameCompletionPercentage = useFrameCompletionPercentage(
    frameBuilderType,
    validationErrorMessages,
  )

  return (
    <FrameBuilderValidationContext.Provider
      value={{
        validationErrorMessages,
        lastValidState,
        enabledItemMap,
        frameCompletionPercentage,
        stepPreviewAnimation,
      }}
    >
      {children}
    </FrameBuilderValidationContext.Provider>
  )
}
